<template>
  <div data-page-name="Installation">
    <ClientOnly>
      <TableOfContent.Mobile
        v-if="tableOfContent"
        :items="tableOfContent"
        :title="contentTableTitle"
        :active-id
      />
    </ClientOnly>

    <div class="wrapper">
      <h1 v-if="postTitle" v-html="postTitle" />

      <Installation.OotbiModelSelect :options="guides" />

      <article
        v-if="postContent"
        v-html="postContent"
        v-photo-swipe="{
          caption: isTouchDevice
            ? $t('ArticleMarkup.zoom-caption-pinch')
            : $t('ArticleMarkup.zoom-caption-scroll', { icon: SvgZoomIn }),
        }"
        v-inject-after="{
          targetSelector: 'table',
          component: Installation.UnboxingSection,
          props: {
            title: post?.data?.data?.video_title,
            url: post?.data?.data?.video_url,
          },
          onInjected: () => {
            videoSectionReady = true;
          },
        }"
        ref="article"
        class="content"
      />

      <aside v-if="resultTableOfContents?.length" ref="toc">
        <div class="focus-toggle">
          <AppToggle v-model="focusMode" />

          <strong v-if="focusModeLabel">
            {{ focusModeLabel }}
          </strong>
        </div>

        <transition name="fade">
          <div v-if="!focusMode">
            <h3 v-if="contentTableTitle">{{ contentTableTitle }}</h3>

            <ul>
              <TableOfContent.Item
                v-for="{ href, title, nested } of resultTableOfContents"
                :key="href"
                :to="href"
                :title
                :active-id
                :nested
              />
            </ul>
          </div>
        </transition>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { showError } from '#app';

// components
import AppToggle from '~/components/redesign/AppToggle.vue';
import Installation from '~/components/redesign/Installation';
import TableOfContent from '~/components/redesign/Installation/TableOfContent';

// directives
import vPhotoSwipe from '~/directives/photoswipe';
import vInjectAfter from '~/directives/inject-after';

// icons
import SvgZoomIn from '~/assets/svg/zoom-in.svg?raw';
import SvgPdfFile from '~/assets/svg/pdf-file.svg?url_encode';

// composable
import { useActiveSectionObserver } from '~/composables/useActiveSectionObserver';
import { useElementSize } from '@vueuse/core';

// utils
import get from 'lodash.get';
import { toSafeArray } from '~/utils';

// types
import { PostStatus, type IContentTable } from '~/types';

const TABLET_TABLE_OF_CONTENTS_OFFSET = 57;
const VIDEO_SECTION_ANCHOR = '#video-title-id';

const router = useRouter();
const {
  params: { slug },
  query: { preview },
} = useRoute();

const { error: guidesError, data: guides } = await useAsyncGql({
  operation: 'InstallationArticles',
  options: {
    transform: data => data.blog_articles.data,
  },
});

if (guidesError.value || !guides.value) {
  showError(`Failed to fetch data.
    Operation: ${guidesError.value?.operationName};
    StatusCode: ${guidesError.value?.statusCode};
    ${guidesError.value?.gqlErrors}`);
}

const routeSlug = computed(() => toSafeArray(slug)[0]);

if (!routeSlug.value && guides.value?.length) {
  router.push(guides.value[0].slug);
}

const { error: guideError, data: post } = await useAsyncGql({
  operation: 'InstallationArticle',
  variables: {
    slug: (routeSlug.value as string) || guides.value[0].slug,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: data => data.blog_article,
  },
});

if (guideError.value || !post.value) {
  showError(`Failed to fetch data.
    Operation: ${guideError.value?.operationName};
    StatusCode: ${guideError.value?.statusCode};
    ${guideError.value?.gqlErrors}`);
}

// utility methods
const isTouchDevice = computed(
  () => import.meta.client && 'ontouchstart' in document,
);
const pdfIconUrl = computed(() => `url("${SvgPdfFile}")`);

const flattenNavItems = <T extends IContentTable>(
  arr: T[],
): Omit<IContentTable, 'nested'>[] => {
  return arr.reduce<Omit<IContentTable, 'nested'>[]>(
    (acc, { href, title, nested = [] }) => {
      acc.push({ href, title });
      if (nested && nested.length > 0) {
        acc = acc.concat(flattenNavItems(nested));
      }
      return acc;
    },
    [],
  );
};

const tocRef = useTemplateRef<HTMLElement>('toc');
const articleRef = useTemplateRef<HTMLElement>('article');

const addDataLabelsToTable = (table: HTMLTableElement) => {
  const headers = table.querySelectorAll('thead th');
  const rows = table.querySelectorAll('tbody tr');

  rows.forEach(row => {
    const cells = row.querySelectorAll('td');

    cells.forEach((cell, cellIndex) => {
      const columnHeader = headers[cellIndex];

      if (columnHeader instanceof HTMLTableCellElement) {
        cell.setAttribute('data-label', columnHeader.innerText.trim());

        const span = document.createElement('span');

        while (cell.firstChild) {
          span.appendChild(cell.firstChild);
        }

        cell.appendChild(span);
      }
    });
  });
};

watchEffect(() => {
  if (!articleRef.value) return;

  articleRef.value.querySelectorAll('table').forEach(addDataLabelsToTable);
});

// state
const focusMode = ref(false);
const videoSectionReady = ref(false);

const resultTableOfContents = ref([]);
const flatTableOfContents = ref([]);

const postTitle = computed(() => get(post.value, 'data.title'));
const postContent = computed(() => get(post.value, 'data.content'));
const focusModeLabel = computed(() => get(post.value, 'data.focus_mode_label'));
const contentTableTitle = computed(() =>
  get(post.value, 'data.data.content_table_title'),
);
const tableOfContent = computed(
  () => get(post.value, 'data.content_table') || [],
);
const videoTitle = computed(() => get(post.value, 'data.data.video_title'));
const videoURL = computed(() => get(post.value, 'data.data.video_url'));

const { activeId } = useActiveSectionObserver(flatTableOfContents);

if (import.meta.client) {
  watch(
    videoSectionReady,
    () => {
      const includesVideoSection = tableOfContent.value.some(
        item => item?.href === VIDEO_SECTION_ANCHOR,
      );

      if (!includesVideoSection && videoTitle.value && videoURL.value) {
        resultTableOfContents.value = [
          ...tableOfContent.value.slice(0, 1),
          {
            href: VIDEO_SECTION_ANCHOR,
            title: `<strong>${videoTitle.value}</strong>`,
          },
          ...tableOfContent.value.slice(1),
        ];
      }

      flatTableOfContents.value = flattenNavItems(resultTableOfContents.value);
    },
    {
      immediate: true,
    },
  );
}

const { height: tocElHeight } = useElementSize(tocRef);
const tocElHeightPx = computed(() => `${tocElHeight.value}px`);

useSeoMeta({
  title: get(post.value, 'data.seo.title'),
  ogTitle: get(post.value, 'data.seo.title'),
  description: get(post.value, 'data.seo.description'),
  ogDescription: get(post.value, 'data.seo.description'),
  ogImage: get(post.value, 'data.seo.image'),
  ogType: get(post.value, 'data.seo.og_type'),
});
</script>

<style lang="scss" scoped>
@import '$/mixins/typography';
@import '$/mixins/size';
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/functions/token';
@import '$/variables/shadows';
@import '$/variables/color-roles';

@mixin link {
  @include link-text-3;
  color: token('link');

  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(token('link'), 16%);
  }
}

@mixin hidden-top-space {
  margin-top: calc(-1 * v-bind(tocElHeightPx));

  @include tablet {
    margin-top: 0;
  }
}

@mixin hidden-bottom-space {
  margin-bottom: v-bind(tocElHeightPx);

  @include tablet {
    margin-bottom: 0;
  }
}

[data-page-name='Installation'] {
  position: relative;

  [data-component-name='TableOfContentMobile'] {
    position: fixed;
    z-index: 5;

    top: var(--scroll-offset);

    outline: 1px solid token('surface');
    background-color: token('surf-cont-primary');
  }

  .wrapper {
    @include wrapper(80rem);
    padding: 4rem 2rem 8rem;
    position: relative;

    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 2fr;
    grid-gap: 3rem 2rem;

    @include tablet {
      padding: 3rem 2rem;
    }

    @include mobile {
      padding: 4rem 1rem;

      grid-template-columns: 1fr;
      grid-gap: 1rem;
    }

    h1 {
      grid-row: 1/1;
      grid-column: 1/4;

      @include h3;
      margin-bottom: 2.5rem;

      @include tablet {
        grid-row: 2/2;
        grid-column: 1/3;
      }

      @include mobile {
        grid-column: 1/1;
        grid-row: 1/1;

        margin-bottom: 0;
      }
    }

    [data-component-name='Installation.OotbiModelSelect'] {
      width: 100%;
      position: sticky;
      align-self: start;

      z-index: 5;

      top: calc(var(--scroll-offset) + 1rem);

      grid-row: 1/1;
      grid-column: 4/4;

      @include hidden-bottom-space;

      @include tablet {
        position: static;

        z-index: 0;

        grid-row: 2/2;
        grid-column: 3 / span 2;

        max-width: 15.125rem;
        justify-self: end;
      }

      @include mobile {
        grid-column: 1/1;
        grid-row: 2/2;

        max-width: 100%;
        margin-bottom: 1rem;
      }
    }

    :deep(article) {
      grid-row: 2/2;
      grid-column: 1/4;

      @include hidden-top-space;

      @include tablet {
        grid-column: 1/5;
        grid-row: 3/3;
      }

      @include mobile {
        grid-column: 1/1;
        grid-row: 3/3;
      }

      h2 {
        @include h4;
        word-wrap: break-word;
        word-break: normal;
      }

      h3 {
        @include subtitle-1;
      }

      h4 {
        @include subtitle-2;
      }

      h5 {
        @include subtitle-3;
      }

      h2,
      h3,
      h4,
      h5 {
        margin-bottom: 2rem;

        @include tablet {
          margin-bottom: 1.5rem;
        }

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      @include tablet {
        [id] {
          scroll-margin-top: calc(
            var(--scroll-offset) +
              v-bind(" TABLET_TABLE_OF_CONTENTS_OFFSET + 'px' ")
          );
        }
      }

      p {
        @include body-3;
        margin-bottom: 1.25rem;

        span {
          margin-bottom: 1.25rem;
        }

        &:has(img) {
          @include flex-center-center;
        }

        img {
          width: 37rem;
          height: auto;

          @include mobile {
            width: 100%;
            height: auto;
          }
        }
      }

      figure.image {
        @include flex-center-center;

        img {
          max-width: 100%;
          width: 37rem;
          height: auto;

          @include mobile {
            width: 100%;
          }
        }
      }

      a {
        @include link;
      }

      ul {
        margin-left: 1rem;
        list-style-type: disc;

        li {
          @include body-3;
        }
      }

      figure.table {
        width: 100%;
        overflow-x: auto;

        table {
          border-spacing: 0;
          border: 0;

          width: 100%;
          height: auto;
          max-height: max-content;

          font-family: map-get($fonts, 'Inter');
          font-size: 1.125rem;

          th,
          td {
            box-sizing: border-box;
            padding: 0.5rem;
            text-align: start;
          }

          thead {
            color: token('text-primary');
            @include subtitle-3;

            tr {
              th {
                border-bottom: 2px solid token('outline-primary');
              }
            }
          }

          tbody {
            th,
            td {
              @include body-3;
              border-bottom: 1px solid token('outline-secondary');

              @include mobile {
                border: none;
              }

              a {
                display: inline-flex;
                gap: 0.25rem;

                &::before {
                  content: v-bind(pdfIconUrl);
                  width: 1rem;
                  height: 1rem;

                  margin-right: 0.5rem;
                }
              }
            }
          }
        }

        @include mobile {
          colgroup,
          thead {
            display: none;
          }

          table {
            border-collapse: separate;
            border-spacing: 0.25rem 2rem;

            tbody {
              tr {
                display: block;
                margin-bottom: 2rem;
                box-shadow: map-get($shadows, 'Elevation 1');

                &:last-of-type {
                  margin-bottom: 0;
                }
              }

              td {
                border: none;
                display: flex;
                justify-content: space-between;
                text-align: end;

                padding: 1rem;
                border: none;

                span {
                  max-width: 50%;

                  word-wrap: break-word;
                  word-break: normal;
                  white-space: normal;

                  hyphens: auto;
                }

                &::before {
                  content: attr(data-label);
                  width: 50%;

                  white-space: nowrap;

                  @include subtitle-3;
                  text-align: start;

                  word-break: normal;
                  white-space: normal;
                  hyphens: auto;
                }
              }
            }
          }
        }

        figcaption {
          @include body-3;
          padding-top: 0.5rem;
          text-align: start;
        }
      }
    }

    aside {
      grid-column: 4/4;
      grid-row: 2/2;

      position: sticky;
      align-self: start;

      top: calc(var(--scroll-offset) + 1rem + 4.625rem);

      @include hidden-top-space;

      @include tablet {
        display: none;
      }

      .focus-toggle {
        @include flex-center-start;
        gap: 0.5rem;
        padding: 0.5rem;

        strong {
          @include body-3;
        }
      }

      h3 {
        @include subtitle-3;

        padding: 0.5rem;
      }
    }
  }
}
</style>
